import { ElementType } from 'react'
import { BackdropIcon, GifIcon, StickerIcon } from '../components/icons'
import { ANIMATED_MIME_TYPES, STILL_MIME_TYPES } from '../constants'
import getLabelFromMimeTypes from '../utils/get-mime-types-label'
import CreationToolOutputType from './output-type'
import CreationToolUserRestriction from './user-restriction'

/**
 * File and url buttons on the welcome screen.
 */
enum CreationToolWelcomeButton {
    Gif = 'CT_WELCOME_BUTTON_GIF',
    Sticker = 'CT_WELCOME_BUTTON_STICKER',
    Backdrop = 'CT_WELCOME_BUTTON_BACKDROP',
}

namespace CreationToolWelcomeButton {
    /**
     * Returns the button's title.
     */
    export function title(button: CreationToolWelcomeButton): string {
        switch (button) {
            case CreationToolWelcomeButton.Gif:
                return 'GIF'
            case CreationToolWelcomeButton.Sticker:
                return 'Sticker'
            case CreationToolWelcomeButton.Backdrop:
                return 'Backdrop'
        }
    }

    /**
     * Returns the button's description.
     */
    export function description(button: CreationToolWelcomeButton): string {
        switch (button) {
            case CreationToolWelcomeButton.Gif:
            case CreationToolWelcomeButton.Sticker:
                return getLabelFromMimeTypes(CreationToolWelcomeButton.mimeTypes(button))
            case CreationToolWelcomeButton.Backdrop:
                return 'Spice up your video calls with our custom virtual background tool.'
        }
    }

    /**
     * Returns the button's icon component.
     */
    export function icon(button: CreationToolWelcomeButton): ElementType {
        switch (button) {
            case CreationToolWelcomeButton.Gif:
                return GifIcon
            case CreationToolWelcomeButton.Sticker:
                return StickerIcon
            case CreationToolWelcomeButton.Backdrop:
                return BackdropIcon
        }
    }

    /**
     * Returns the button's url if it has one.
     */
    export function url(button: CreationToolWelcomeButton): string {
        switch (button) {
            case CreationToolWelcomeButton.Backdrop:
                return '/backdrop'
            default:
                return ''
        }
    }

    /**
     * Returns the button's accepted mimeTypes.
     */
    export function mimeTypes(button: CreationToolWelcomeButton): string[] {
        switch (button) {
            case CreationToolWelcomeButton.Gif:
                return [...STILL_MIME_TYPES, ...ANIMATED_MIME_TYPES]
            case CreationToolWelcomeButton.Sticker:
                return [...STILL_MIME_TYPES]
            default:
                return []
        }
    }

    /**
     * Returns the button's creation output type.
     */
    export function outputType(button: CreationToolWelcomeButton): CreationToolOutputType {
        switch (button) {
            case CreationToolWelcomeButton.Gif:
                return CreationToolOutputType.Gif
            case CreationToolWelcomeButton.Sticker:
                return CreationToolOutputType.Sticker
            case CreationToolWelcomeButton.Backdrop:
                return CreationToolOutputType.Backdrop
        }
    }

    /**
     * Returns user restrictions for the output type, if it has one.
     */
    export function userRestriction(button: CreationToolWelcomeButton): CreationToolUserRestriction | null {
        switch (button) {
            default:
                return CreationToolOutputType.userRestriction(CreationToolWelcomeButton.outputType(button))
        }
    }

    /**
     * Returns whether or not the button is new.
     */
    export function isNew(button: CreationToolWelcomeButton): boolean {
        switch (button) {
            case CreationToolWelcomeButton.Sticker:
                return true
            default:
                return false
        }
    }
}

export default CreationToolWelcomeButton
